import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import {
  DigitalCardSettingsData,
  EmployeeWithBrand,
  WalletSettingsData,
} from "./card-digital.interface";
import type {
  DigitalCardProfileAnalytics,
  DigitalCardProfileDetail,
  DigitalCardProfileItem,
  DigitalCardProfileReadiness,
  DigitalCardProfilesResponse,
  DigitalCardDomainAnalytics,
  DigitalCardCustomSourceItem,
} from "./digital-cards.interface";

@Injectable({
  providedIn: "root",
})
export class DigitalCardService {
  constructor(private http: HttpClient) {}

  getCurrentEmployee(): Observable<{ employee: EmployeeWithBrand }> {
    return this.http.get<{ employee: EmployeeWithBrand }>(
      "/card-digital/current-employee",
    );
  }

  getPreviewEmployee(
    email: string,
  ): Observable<{ employee: EmployeeWithBrand }> {
    return this.http.get<{ employee: EmployeeWithBrand }>(
      "/card-digital/preview-employee",
      {
        params: new HttpParams().set("email", email.trim()),
      },
    );
  }

  uploadCardBanner(file: File): Observable<{
    bannerImageUrl: string;
    bannerPosterUrl: string | null;
    bannerIsAnimated: boolean;
  }> {
    const form = new FormData();
    form.append("image", file);
    return this.http.post<{
      bannerImageUrl: string;
      bannerPosterUrl: string | null;
      bannerIsAnimated: boolean;
    }>("/card-digital/banner", form);
  }

  uploadCardBannerPoster(file: File): Observable<{ bannerPosterUrl: string }> {
    const form = new FormData();
    form.append("image", file);
    return this.http.post<{ bannerPosterUrl: string }>(
      "/card-digital/banner-poster",
      form,
    );
  }

  getProfiles(options?: {
    templatesOnly?: boolean;
    forEmail?: string;
    forTemplate?: string;
    mine?: boolean;
  }): Observable<DigitalCardProfilesResponse> {
    let params = new HttpParams();
    if (options?.templatesOnly === true) {
      params = params.set("templatesOnly", "true");
    }
    const forEmail = (options?.forEmail ?? "").trim();
    if (forEmail) {
      params = params.set("forEmail", forEmail);
    }
    const forTemplate = (options?.forTemplate ?? "").trim();
    if (forTemplate) {
      params = params.set("forTemplate", forTemplate);
    }
    if (options?.mine === true) {
      params = params.set("mine", "true");
    }
    return this.http.get<DigitalCardProfilesResponse>(
      "/card-digital/profiles",
      { params },
    );
  }

  createProfile(
    displayName?: string,
  ): Observable<
    DigitalCardProfileItem & { domain?: string; publicUrl?: string }
  > {
    return this.http.post<
      DigitalCardProfileItem & { domain?: string; publicUrl?: string }
    >("/card-digital/profiles", { displayName: displayName ?? null });
  }

  setDefaultProfile(
    profileId: string,
  ): Observable<
    Pick<
      DigitalCardProfileItem,
      "profileId" | "displayName" | "status" | "isDefault"
    >
  > {
    return this.http.post<
      Pick<
        DigitalCardProfileItem,
        "profileId" | "displayName" | "status" | "isDefault"
      >
    >(`/card-digital/profiles/${encodeURIComponent(profileId)}/default`, {});
  }

  getProfile(profileId: string): Observable<DigitalCardProfileDetail> {
    return this.http.get<DigitalCardProfileDetail>(
      `/card-digital/profiles/${encodeURIComponent(profileId)}`,
    );
  }

  getProfileReadiness(
    profileId: string,
    mine?: boolean,
  ): Observable<DigitalCardProfileReadiness> {
    let params = new HttpParams();
    if (mine) params = params.set("mine", "true");
    return this.http.get<DigitalCardProfileReadiness>(
      `/card-digital/profiles/${encodeURIComponent(profileId)}/readiness`,
      { params },
    );
  }

  updateProfile(
    profileId: string,
    payload: {
      settings: DigitalCardSettingsData;
      walletSettings?: WalletSettingsData;
      displayName?: string;
      employee?: unknown;
      hash?: string;
      email?: string;
    },
  ): Observable<DigitalCardProfileDetail> {
    return this.http.put<DigitalCardProfileDetail>(
      `/card-digital/profiles/${encodeURIComponent(profileId)}`,
      payload,
    );
  }

  deleteProfile(profileId: string): Observable<void> {
    return this.http.delete<void>(
      `/card-digital/profiles/${encodeURIComponent(profileId)}`,
    );
  }

  getProfileAnalytics(
    profileId: string,
    from?: string,
    to?: string,
    mine?: boolean,
  ): Observable<DigitalCardProfileAnalytics> {
    let params = new HttpParams();
    if (from) params = params.set("from", from);
    if (to) params = params.set("to", to);
    if (mine) params = params.set("mine", "true");
    return this.http.get<DigitalCardProfileAnalytics>(
      `/card-digital/profiles/${encodeURIComponent(profileId)}/analytics`,
      { params },
    );
  }

  getDomainAnalytics(options?: {
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    order?: "ASC" | "DESC";
  }): Observable<DigitalCardDomainAnalytics> {
    let params = new HttpParams();
    if (options?.from) params = params.set("from", options.from);
    if (options?.to) params = params.set("to", options.to);
    if (options?.page != null)
      params = params.set("page", String(options.page));
    if (options?.limit != null)
      params = params.set("limit", String(options.limit));
    if (options?.search?.trim())
      params = params.set("search", options.search.trim());
    if (options?.sortBy?.trim()) params = params.set("sortBy", options.sortBy);
    if (options?.order) params = params.set("order", options.order);
    return this.http.get<DigitalCardDomainAnalytics>(
      "/card-digital/analytics/domain",
      {
        params,
      },
    );
  }

  downloadDomainAnalyticsCsv(
    from?: string,
    to?: string,
    lang?: string,
  ): Observable<Blob> {
    let params = new HttpParams().set("format", "csv");
    if (from) params = params.set("from", from);
    if (to) params = params.set("to", to);
    if (lang) params = params.set("lang", lang);
    return this.http.get("/card-digital/analytics/domain", {
      params,
      responseType: "blob",
    });
  }

  getSourceTokens(
    profileId: string,
    mediums: string[],
  ): Observable<Record<string, { token: string; sourceUrl: string }>> {
    return this.http.post<Record<string, { token: string; sourceUrl: string }>>(
      `/card-digital/profiles/${encodeURIComponent(profileId)}/source-tokens`,
      { mediums },
    );
  }

  getSourceTokensBulk(
    profileIds: string[],
    mediums: string[],
  ): Observable<
    Record<string, Record<string, { token: string; sourceUrl: string }>>
  > {
    return this.http.post<
      Record<string, Record<string, { token: string; sourceUrl: string }>>
    >("/card-digital/profiles/source-tokens", { profileIds, mediums });
  }

  getEntryLink(
    profileId: string,
    mine?: boolean,
  ): Observable<{ entryHash: string; entryUrl: string }> {
    let params = new HttpParams();
    if (mine) params = params.set("mine", "true");
    return this.http.post<{ entryHash: string; entryUrl: string }>(
      `/card-digital/profiles/${encodeURIComponent(profileId)}/entry`,
      {},
      { params },
    );
  }

  listCustomSources(): Observable<{ sources: DigitalCardCustomSourceItem[] }> {
    return this.http.get<{ sources: DigitalCardCustomSourceItem[] }>(
      "/card-digital/custom-sources",
    );
  }

  createCustomSource(name: string): Observable<DigitalCardCustomSourceItem> {
    return this.http.post<DigitalCardCustomSourceItem>(
      "/card-digital/custom-sources",
      { name },
    );
  }

  deleteCustomSource(customSourceId: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(
      `/card-digital/custom-sources/${encodeURIComponent(customSourceId)}`,
    );
  }

  sendEntryLinkMessage(
    profileId: string,
    params: { to: string; name: string; company: string },
    mine?: boolean,
  ): Observable<{
    ok: boolean;
  }> {
    let httpParams = new HttpParams();
    if (mine) httpParams = httpParams.set("mine", "true");
    return this.http.post<{
      ok: boolean;
    }>(
      `/card-digital/profiles/${encodeURIComponent(profileId)}/entry/email`,
      {
        to: params.to,
        name: params.name,
        company: params.company,
      },
      { params: httpParams },
    );
  }
}
